// The axios response interceptor is registered once at module load in index.tsx, outside React,
// so it can't call into a context directly. SessionExpiredProvider registers its handler here and
// the interceptor reaches it through this bridge.

export type SessionExpiredOrigin = 'token-refresh' | 'token-configure' | 'token-get' | 'api-401';

type Handler = (origin: SessionExpiredOrigin) => void;

let handler: Handler | undefined;

export function registerSessionExpiredHandler(next: Handler) {
  handler = next;
  return () => {
    if (handler === next) handler = undefined;
  };
}

export function reportSessionExpired(origin: SessionExpiredOrigin) {
  handler?.(origin);
}

// A 401 usually means the global axios header went stale while the tab slept, not that the session
// died — the refresh token is often still good. useConfigureAccessToken registers a refresher here
// so the interceptor can swap in a fresh token and retry before telling the user anything.

type TokenRefresher = () => Promise<string | undefined>;

let refresher: TokenRefresher | undefined;
let inFlight: Promise<string | undefined> | undefined;

export function registerTokenRefresher(next: TokenRefresher) {
  refresher = next;
  return () => {
    if (refresher === next) refresher = undefined;
  };
}

export function hasTokenRefresher() {
  return refresher !== undefined;
}

// Coming back to a sleeping tab fires a burst of requests that all 401 together; they share one
// refresh rather than each running their own.
export function refreshAccessToken(): Promise<string | undefined> {
  if (!refresher) return Promise.resolve(undefined);
  if (!inFlight) {
    inFlight = refresher().finally(() => {
      inFlight = undefined;
    });
  }
  return inFlight;
}
